import React from 'react';
import { ArrowRight, ExternalLink, ChevronRight } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function OnTeams() {
  const {
    language
  } = useLanguage();
  const isIS = language === 'is';
  const en = {
    back: 'Writing',
    badge: 'Four articles',
    title: 'On Teams',
    intro: `Teams are the basic unit of how most organisations get things done. And yet most organisations treat team formation as an HR formality, team development as a once-a-year away day, and team failure as an individual performance problem.

The research on what actually makes teams work is not new. Hackman and Wageman's work on team effectiveness has been in the literature for decades. It is unusually consistent. And it is almost universally ignored - not because it is difficult to understand, but because what it points to is inconvenient. The research says that most of what determines whether a team succeeds or fails is in place before the team ever has its first meeting. It is structural. It is the leader's responsibility. And it has very little to do with team-building exercises.

There is also a more fundamental confusion underneath the team effectiveness question, which is that most of what organisations call teams are not actually teams. A group of people who share a manager, attend the same meetings, and sit in the same part of the office is not automatically a team. The distinction matters, because the conditions that turn a group into a team are specific, requiring something of leaders that most leadership development programmes have never addressed.

This series looks at what the research actually says - and what it genuinely demands.`,
    articles: [{
      n: '01',
      title: 'Groups Are Not Teams',
      desc: 'The distinction matters enormously. A group of people sharing a manager is not automatically a team. What it takes to become one - and why so few do.',
      live: false
    }, {
      n: '02',
      title: 'The Conditions for Team Effectiveness',
      desc: "Hackman and Wageman's research on what actually predicts team performance - and how much of it has nothing to do with who is on the team.",
      live: false
    }, {
      n: '03',
      title: 'The Leadership Implication',
      desc: 'What the team effectiveness research demands of a leader. Not what most leadership development programmes teach.',
      live: false
    }, {
      n: '04',
      title: 'When Teams Change',
      desc: 'Dynamic reteaming and the question most organisations get wrong: when is changing the team composition the right answer, and when is it avoidance?',
      live: false
    }],
    substackCta: 'Follow on Substack - series publishing soon',
    relatedTitle: 'Related',
    related: [{
      label: 'First-Time Manager',
      href: '/services/courses/first-time-manager'
    }, {
      label: 'Coaching Skills for Managers',
      href: '/services/workshops/coaching-skills'
    }]
  };
  const is = {
    back: 'Skrif',
    badge: 'Fjórar greinar',
    title: 'Um teymi',
    intro: `Teymið: Grunneiningin sem oftast er misskilin

Teymi eru grunneiningin í því hvernig flestar skipulagsheildir fá hlutina gerða. Samt líta flest fyrirtæki á myndun teyma sem formsatriði hjá mannauðsdeildinni, teymisþróun sem árlegan starfsdag og mistök teyma sem vandamál tengd frammistöðu einstaklinga.

Rannsóknir á því hvað raunverulega fær teymi til að virka eru ekki nýjar af nálinni. Verk Hackman og Wageman um skilvirkni teyma hafa verið í fræðunum í áratugi. Niðurstöður þeirra eru óvenju samkvæmar sjálfum sér. Samt eru þær nánast almennt hunsaðar – ekki vegna þess að þær séu flóknar, heldur vegna þess að það sem þær benda á er óþægilegt. Rannsóknir sýna að flest það sem ræður úrslitum um hvort teymi tekst upp eða mistekst, er þegar komið á áður en teymið heldur sinn fyrsta fund. Það er skipulagslegt. Það er á ábyrgð leiðtogans. Og það tengist teymiseflingaræfingum nánast ekki neitt.

Undir spurningunni um skilvirkni teyma liggur líka dýpri ruglingur: flest það sem skipulagsheildir kalla teymi eru í raun ekki teymi. Hópur fólks sem deilir sama yfirmanni, mætir á sömu fundi og situr á sama stað á skrifstofunni er ekki sjálfkrafa teymi. Þessi aðgreining skiptir máli, því skilyrðin sem breyta hópi í teymi eru sértæk og krefjast þess af leiðtogum sem flest stjórnendaþjálfunarkerfi hafa aldrei tekið fyrir.

Í þessari seríu er skoðað hvað rannsóknirnar segja í raun – og hvers þær raunverulega krefjast.`,
    articles: [{
      n: '01',
      title: 'Hópar eru ekki teymi',
      desc: 'Munurinn skiptir geysilega máli. Hópur fólks sem deilir stjórnanda er ekki sjálfkrafa teymi. Hvað það krefst að verða eitt - og hvers vegna svo fátt gerist.',
      live: false
    }, {
      n: '02',
      title: 'Skilyrðin fyrir teymisárangur',
      desc: 'Rannsóknir Hackman og Wageman á hvað spáir raunverulega fyrir um teymisframmistöðu - og hversu mikið af þí hefur ekkert með það að gera hverjir eru í teyminu.',
      live: false
    }, {
      n: '03',
      title: 'Hvað þetta þýðir fyrir leiðtoga',
      desc: 'Hvað rannsóknirnar á teymisárangri krefjast af leiðtoga. Ekki hvað flestar leiðtogaþróunarnámskeið kenna.',
      live: false
    }, {
      n: '04',
      title: 'Þegar teymi breytast',
      desc: 'Kvikt endurteymi og spurningin sem flestar stofnanir svara rangt: hvenær er breyting á teymissamsetningunni réttur kostur, og hvenær er það forðun?',
      live: false
    }],
    substackCta: 'Fylgjast á Substack - röðin kemur fljótlega',
    relatedTitle: 'Tengt',
    related: [{
      label: 'Stjórnandi í fyrsta sinn',
      href: '/services/courses/first-time-manager'
    }, {
      label: 'Coaching Skills for Managers',
      href: '/services/workshops/coaching-skills'
    }]
  };
  const c = isIS ? is : en;
  return <PageTransition data-id="element-2497" data-mp="17t2d8r3hw04w">
      <div className="pt-12 pb-24" data-id="element-2498" data-mp="17tlv67t5aoix">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2499" data-mp="17qg62skinmn7">
          <Animate data-id="element-2500" data-mp="25eq837kayaeb">
            <Link to="/writing" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal mb-8 transition-colors" data-id="element-2501" data-mp="25dyq4zjmob6i">
              <ChevronRight size={14} className="rotate-180" data-id="element-2502" data-mp="1vgai57x0h76e" />
              {c.back}
            </Link>
          </Animate>
          <Animate data-id="element-2503" data-mp="1qkv5ukenjjc3">
            <span className="inline-block px-3 py-1 bg-teal/10 text-teal font-medium rounded-full text-xs mb-4" data-id="element-2504" data-mp="k2ovpuijdjok">
              {c.badge}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-charcoal mb-6" data-id="element-2505" data-mp="f2298g55ywd8">
              {c.title}
            </h1>
            <div className="prose prose-lg text-gray-500 leading-relaxed mb-12 space-y-4" data-id="element-2506" data-mp="2qow0q10uhdxr">
              {c.intro.split('\n\n').map((para, i) => <p key={i} data-id="element-2507" data-mp="30ov5rny22m5v" data-mpi={i}>{para}</p>)}
            </div>
          </Animate>
          <div className="space-y-4 mb-12" data-id="element-2508" data-mp="35gdbsqexrcxp">
            {c.articles.map((a, i) => <Animate key={a.n} delay={i * 0.05} data-id="element-2509" data-mp="3af80ua3hxxq9" data-mpi={i}>
                <Card className="p-6 border border-gray-100 opacity-70" data-id="element-2510" data-mp="3aeepswxi8m2b">
                  <div className="flex items-start gap-4" data-id="element-2511" data-mp="3efi7zq30yu6p">
                    <span className="text-2xl font-bold text-gray-200 font-mono flex-shrink-0 leading-none mt-1" data-id="element-2512" data-mp="3jfmp9ibrbsxz">
                      {a.n}
                    </span>
                    <div className="flex-1" data-id="element-2513" data-mp="3odxz3t21y3g7">
                      <div className="flex items-center gap-2 mb-1" data-id="element-2514" data-mp="1p24ylmg22fyq">
                        <h2 className="font-bold text-charcoal" data-id="element-2515" data-mp="1p3y6t66bh88u">{a.title}</h2>
                        <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full font-medium" data-id="element-2516" data-mp="1u0ydnjazqf10">
                          Coming soon
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm leading-relaxed" data-id="element-2517" data-mp="3qx2r7u1tt3dz">
                        {a.desc}
                      </p>
                    </div>
                  </div>
                </Card>
              </Animate>)}
          </div>
          <Animate data-id="element-2518" data-mp="3cykyfhx2d0p7">
            <Card className="p-6 bg-teal/5 border-teal/20 border text-center mb-12" data-id="element-2519" data-mp="3czwae50nxere">
              <p className="text-gray-600 mb-4" data-id="element-2520" data-mp="381ck5sf1dgt8">{c.substackCta}</p>
              <a href="https://helgig.substack.com" target="_blank" rel="noopener noreferrer" data-id="element-2521" data-mp="3mxah3cwsap6i">
                <Button variant="outline" rightIcon={<ExternalLink size={16} data-id="element-2523" data-mp="1269u6nvlr9gw" />} data-id="element-2522" data-mp="3hx5x3cwo3cbj">
                  Substack
                </Button>
              </a>
            </Card>
          </Animate>
          <Animate data-id="element-2524" data-mp="14s0yx2x4jf3r">
            <div data-id="element-2525" data-mp="14qpsevcant8y">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2526" data-mp="14q69bbawqytc">
                {c.relatedTitle}
              </h3>
              <div className="flex flex-wrap gap-3" data-id="element-2527" data-mp="vrcgzx4m0ull">
                {c.related.map(r => <Link key={r.href} to={r.href} data-id="element-2528" data-mp="zsoc2i696816">
                    <Button variant="outline" size="sm" rightIcon={<ArrowRight size={14} data-id="element-2530" data-mp="1nj0q8as3d107" />} data-id="element-2529" data-mp="ztfxx1a1sre9">
                      {r.label}
                    </Button>
                  </Link>)}
              </div>
            </div>
          </Animate>
        </div>
      </div>
    </PageTransition>;
}